import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { MobileAuthService } from '../services/auth.service';
import { MobileReputationService } from '../services/reputation.service';
import { MobileThemeService } from '../shared/services/theme.service';
import { MobileReputationComponent } from '../components/reputation/reputation.component';

@Component({
  standalone: true,
  selector: 'reputation-mini-card',
  imports: [CommonModule, TranslatePipe],
  templateUrl: './reputation-mini-card.component.html',
  styleUrls: ['./reputation-mini-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileReputationMiniCardComponent extends MobileReputationComponent implements OnInit {
  @Input() clickable = true;
  @Input() isSidebar = false;
  @Input() zoom: string = '100%';
  @Input() navigateOnClick = true;
  @Input() reputationRoute = '/reputation';
  @Input() darkTheme: { [key: string]: string } = {
    '--bg-dark': '#222032',
  };
  
  @Output() cardClick = new EventEmitter<void>();

  private readonly elementRef = inject(ElementRef);

  cardInfoStyle: Record<string, string> = {};

  constructor(
    router: Router,
    cd: ChangeDetectorRef,
    translate: TranslateService,
    authService: MobileAuthService,
    themeService: MobileThemeService,
    reputationService: MobileReputationService,
  ) {
    super(router, cd, translate, authService, themeService, reputationService);
  }

  get currentStatusIconUrl(): string | null {
    return this.resolvePictureUrl(this.currentLevel?.picture) || this.preLevelPictureUrl || this.loyaltyIconSrc;
  }

  get currentStatusTitle(): string {
    if (this.currentLevel) {
      return this.getLevelTitle(this.currentLevel);
    }
    return this.preLevelTitle;
  }

  get nextStatusTitle(): string {
    if (this.isFinalLevel) {
      return this.currentStatusTitle;
    }
    return this.getLevelTitle(this.nextLevel) || this.currentStatusTitle;
  }

  get currentPointsLabel(): string {
    return this.formatPoints(Math.max(0, Math.round(+(this.wallet?.amount ?? 0))));
  }

  get targetPointsLabel(): string {
    if (this.isFinalLevel) {
      return this.currentPointsLabel;
    }
    const target = +(this.nextLevel?.levelInterval?.max ?? this.wallet?.amount ?? 0);
    return this.formatPoints(Math.max(0, Math.round(target)));
  }

  get remainingPointsLabel(): string {
    return this.formatPoints(Math.max(0, Math.round(this.remainingToNextLevel || 0)));
  }

  get progressValue(): number {
    if (!Number.isFinite(this.currentLevelPercentage)) {
      return 0;
    }
    return Math.max(0, Math.min(100, this.currentLevelPercentage));
  }

  get statusAccentColor(): string {
    return this.getLevelColor(this.currentLevel) || this.preLevel?.color || this.nextLevel?.color || '#2563eb';
  }

  onCardClick(): void {
    if (!this.clickable || this.loading) {
      return;
    }

    this.cardClick.emit();

    if (this.navigateOnClick) {
      void this.router.navigate([this.reputationRoute]);
    }
  }

  onCardKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.onCardClick();
  }

  protected override rebuildReputationLevels(levels = this.reputationLevels): void {
    super.rebuildReputationLevels(levels);
    this.updateCardTheme();
  }

  private updateCardTheme(): void {
    const bg = (this.getLevelColor(this.currentLevel) || this.preLevel?.color || '').toString().trim();
    const isDark = this.isDarkColor(bg);
    const text = isDark ? '#ffffff' : '#0f172a';
    const subtext = isDark ? 'rgba(255,255,255,0.86)' : 'rgba(15,23,42,0.72)';

    const hostEl: HTMLElement = this.elementRef.nativeElement;
    hostEl.style.setProperty('--card-text', text);
    hostEl.style.setProperty('--card-subtext', subtext);

    this.cardInfoStyle = {
      background: bg,
      '--card-text': text,
      '--card-subtext': subtext,
    };
  }

  private resolvePictureUrl(picture?: { baseUrl?: string | null; path?: string | null } | null): string | null {
    if (picture?.baseUrl && picture?.path) {
      return `${picture.baseUrl}/${picture.path}`;
    }
    return null;
  }

  private isDarkColor(input: string): boolean {
    const rgb = this.parseColorToRgb(input);
    if (!rgb) {
      return true;
    }

    const toLinear = (value: number) => {
      const channel = value / 255;
      return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    };

    const r = toLinear(rgb.r);
    const g = toLinear(rgb.g);
    const b = toLinear(rgb.b);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance < 0.4;
  }

  private parseColorToRgb(input: string): { r: number; g: number; b: number } | null {
    if (!input) {
      return null;
    }

    const value = input.trim();
    const hex = value.startsWith('#') ? value.slice(1) : '';

    if (hex) {
      if (hex.length === 3) {
        return {
          r: parseInt(hex[0] + hex[0], 16),
          g: parseInt(hex[1] + hex[1], 16),
          b: parseInt(hex[2] + hex[2], 16),
        };
      }

      if (hex.length === 6 || hex.length === 8) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16),
        };
      }
    }

    const rgbMatch = value.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+)?\s*\)$/i);
    if (!rgbMatch) {
      return null;
    }

    return {
      r: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[1])))),
      g: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[2])))),
      b: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[3])))),
    };
  }
}
